import React, { useEffect, useRef, useState } from 'react';
import { vehicleApi } from '../../api/portal';
import { invalidatePortal } from '../../hooks/usePortalQuery';

const name = (profile) => `${profile.plate_number || profile.id} (${profile.id})`;
const ids = (value) => Array.isArray(value) && value.every((id) => typeof id === 'string');
const validProgress = (value) => value && typeof value.target_updated === 'boolean' &&
  ids(value.moved_cost_ids) && ids(value.deleted_source_ids) &&
  (value.moved_reservation_ids === undefined || ids(value.moved_reservation_ids)) &&
  (value.legacy_reservation_ids === undefined || ids(value.legacy_reservation_ids));

export default function ProfileMerge({ profiles, investors, investorsLoading, onSelectionChange, onBusyChange, onMerged }) {
  const [chosenTarget, setChosenTarget] = useState('');
  const [investorId, setInvestorId] = useState('');
  const [allowDifferentPlates, setAllowDifferentPlates] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState(false);
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);
  const [error, setError] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const targetId = profiles.some((profile) => profile.id === chosenTarget) ? chosenTarget : profiles[0].id;
  const sourceIds = profiles.filter((profile) => profile.id !== targetId).map((profile) => profile.id);
  const request = {
    target_id: targetId, source_ids: sourceIds,
    ...(investorId ? { investor_id: investorId } : {}),
    allow_different_plates: allowDifferentPlates,
  };
  const key = JSON.stringify(request);
  const visiblePreview = preview?.key === key ? preview : null;
  useEffect(() => { setPreview(null); setConfirmation(false); }, [key]);
  const ownerName = (id) => investors.find((investor) => investor.id === id)?.name || id || 'Unassigned';
  const invalidatePreview = () => { setPreview(null); setConfirmation(false); setError(null); };
  const change = (update) => { invalidatePreview(); update(); };

  const run = async (commit) => {
    if (busyRef.current || recovery || !sourceIds.length || sourceIds.length > 20 ||
      investorsLoading || (commit && (!visiblePreview || !confirmation))) return;
    busyRef.current = true; setBusy(commit ? 'Merging...' : 'Previewing...'); onBusyChange(true);
    setError(null); setConfirmation(false);
    const payload = { ...(commit ? visiblePreview.request : request), dry_run: !commit };
    let needsRecovery = false;
    setPreview(null);
    try {
      const result = await vehicleApi.merge(payload);
      if (commit) {
        invalidatePortal();
        onMerged(result.target.id);
      } else {
        setPreview({ key, request, result });
      }
    } catch (requestError) {
      setError({ message: requestError.message || 'Unable to merge profiles.', payload: requestError.payload, status: requestError.status });
      if (commit && (!requestError.status || requestError.status >= 500)) {
        needsRecovery = true;
        invalidatePortal();
        const progress = requestError.payload?.progress;
        if (validProgress(progress) && progress.deleted_source_ids.every((id) => payload.source_ids.includes(id))) {
          setRecovery({ progress });
        } else {
          setRecovery({ unknown: true });
        }
      }
    } finally { busyRef.current = false; setBusy(''); onBusyChange(needsRecovery); }
  };
  const reviewRecovery = () => {
    invalidatePreview();
    if (recovery.unknown) {
      onSelectionChange([]);
    } else {
      onSelectionChange(profiles.filter((profile) => !recovery.progress.deleted_source_ids.includes(profile.id)));
    }
    setRecovery(null);
    onBusyChange(false);
    invalidatePortal();
  };
  const result = visiblePreview?.result;
  return <section className="portal-card"><h2>Merge car profiles</h2>
    <p>{profiles.length} selected profiles. Selections stay selected across searches and pages; choose one surviving target and up to 20 sources.</p>
    <ul className="portal-merge-list">{profiles.map((profile) => <li key={profile.id}><span>{name(profile)} - owner: {ownerName(profile.Owner_investor_id)}</span>
      <button type="button" className="portal-link-button" disabled={!!busy || !!recovery} onClick={() => change(() => onSelectionChange(profiles.filter((item) => item.id !== profile.id)))}
        aria-label={`Remove ${profile.id} from merge`}>Remove</button></li>)}</ul>
    <button type="button" className="portal-link-button" disabled={!!busy || !!recovery} onClick={() => onSelectionChange([])}>Clear selection</button>
    <div className="portal-form portal-merge-form">
      <label>Surviving profile<select disabled={!!busy || !!recovery} value={targetId} onChange={(event) => change(() => setChosenTarget(event.target.value))}>
        {profiles.map((profile) => <option key={profile.id} value={profile.id}>{name(profile)}</option>)}
      </select></label>
      <div className="portal-field"><label>Merged owner<select disabled={!!busy || !!recovery || investorsLoading} value={investorId}
        aria-describedby="merge-owner-help" onChange={(event) => change(() => setInvestorId(event.target.value))}>
        <option value="">Preserve existing owner (if compatible)</option>
        {investors.map((investor) => <option key={investor.id} value={investor.id}>{investor.name}</option>)}
      </select></label>
        <p id="merge-owner-help" className="portal-muted">Different existing owners require selecting the merged owner. This choice assigns or replaces ownership; it does not clear ownership. All manual costs will be attributed to the merged current owner.</p></div>
      <div className={`portal-option${allowDifferentPlates ? ' portal-option-checked' : ''}`}>
        <label className="portal-check"><input type="checkbox" disabled={!!busy || !!recovery} checked={allowDifferentPlates}
          aria-describedby="merge-different-plates-help"
          onChange={(event) => change(() => setAllowDifferentPlates(event.target.checked))} />
          Allow different plates</label>
        <p id="merge-different-plates-help" className="portal-muted">Only check this if the selected profiles have different plate numbers and you still want to merge them. Merged-away plates will no longer resolve, and future photos or imports for those plates may create or require another profile.</p>
      </div>
      <p className="portal-warning portal-full">Sources will be deleted. The target keeps its metadata and fills missing fields from recent sources. Costs and all reservations move to the target, including legacy photos. The profile's photo links mirror its newest reservation; older photos remain in reservation history. Imported statements remain unchanged. Do not run concurrent uploads, imports, ownership changes, or merges for these cars.</p>
      <div className="portal-actions"><button type="button" disabled={!!busy || !!recovery || investorsLoading || !sourceIds.length || sourceIds.length > 20}
        onClick={() => run(false)}>{busy || 'Preview merge'}</button></div>
    </div>
    {error && <div className="portal-error" role="alert"><p>{error.message}</p>
      {error.status === 409 && <p>Resolve the conflict, then preview again. Select a merged owner for conflicting owners; only override different plates after reviewing the warning.</p>}
      {error.payload?.target_plate && <p>Target plate: {error.payload.target_plate}</p>}
      {ids(error.payload?.source_plates) && <p>Conflicting source plates: {error.payload.source_plates.join(', ')}</p>}
      {ids(error.payload?.owners) && <p>Conflicting owners: {error.payload.owners.map(ownerName).join(', ')}</p>}
    </div>}
    {recovery && <div className="portal-warning" role="region" aria-label="Merge recovery">
      <h3>Review before retrying</h3>
      {recovery.unknown ? <p>The merge outcome is unverified. Reload and inspect the target, sources, and costs before selecting remaining profiles and making a fresh preview. No write will be automatically retried.</p> : <>
        <p>The merge may have partially applied. Inspect the target and costs before retrying.</p>
        <dl><dt>Target updated</dt><dd>{recovery.progress.target_updated ? 'Yes' : 'No'}</dd>
          <dt>Moved cost IDs</dt><dd>{recovery.progress.moved_cost_ids.join(', ') || 'None reported'}</dd>
          {recovery.progress.moved_reservation_ids !== undefined && <><dt>Moved reservation IDs</dt><dd>{recovery.progress.moved_reservation_ids.join(', ') || 'None reported'}</dd></>}
          {recovery.progress.legacy_reservation_ids !== undefined && <><dt>Saved legacy reservation IDs</dt><dd>{recovery.progress.legacy_reservation_ids.join(', ') || 'None reported'}</dd></>}
          <dt>Deleted source IDs</dt><dd>{recovery.progress.deleted_source_ids.join(', ') || 'None reported'}</dd></dl>
        <p>Retry selection will exclude deleted sources. A fresh preview and confirmation are required.</p>
      </>}
      <button type="button" onClick={reviewRecovery}>{recovery.unknown ? 'Reload and clear unverified selection' : 'Acknowledge progress and reload remaining profiles'}</button>
    </div>}
    {result && <div role="region" aria-label="Merge preview"><h3>Merge preview</h3>
      <dl><dt>Surviving profile</dt><dd>{name(result.target)}</dd>
        <dt>Merged owner</dt><dd>{ownerName(result.target.Owner_investor_id)}</dd>
        <dt>Make / model</dt><dd>{[result.target.Make, result.target.Model].filter(Boolean).join(' ') || 'Not provided'}</dd>
        <dt>Year</dt><dd>{result.target.year || 'Not provided'}</dd>
        <dt>Description</dt><dd>{result.target.description || 'Not provided'}</dd>
        <dt>Vehicle name</dt><dd>{result.target.vehicle_name || 'Not provided'}</dd>
        <dt>VIN</dt><dd>{result.target.vin || 'Not provided'}</dd>
        <dt>Sources to delete</dt><dd>{result.source_ids.join(', ')}</dd>
        {result.moved_reservation_ids !== undefined && <><dt>Reservations to move ({result.moved_reservation_ids.length})</dt><dd>{result.moved_reservation_ids.join(', ') || 'None'}</dd></>}
        {result.legacy_reservation_ids !== undefined && <><dt>Legacy reservations to save ({result.legacy_reservation_ids.length})</dt><dd>{result.legacy_reservation_ids.join(', ') || 'None'}</dd></>}
        <dt>Costs to move ({result.moved_cost_ids.length})</dt><dd>{result.moved_cost_ids.join(', ') || 'None'}</dd></dl>
      <h4>Latest reservation photo links ({result.target.Picture_links.length})</h4>
      {result.target.Picture_links.length ? <ul>{result.target.Picture_links.map((link, index) => <li key={`${index}-${link}`}>{link}</li>)}</ul> : <p>No photo links.</p>}
      {result.warnings.length > 0 && <div className="portal-warning"><h4>Merge warnings</h4><ul>{result.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
      {!confirmation ? <button type="button" disabled={!!busy || investorsLoading} onClick={() => setConfirmation(true)}>Review merge confirmation</button> :
        <div className="portal-warning" role="region" aria-label="Merge confirmation">
          <p>Keep {name(result.target)}, delete {result.source_ids.length} source profiles, and use {ownerName(result.target.Owner_investor_id)} as the current owner? This merge has no automatic rollback.</p>
          <div className="portal-actions"><button type="button" disabled={!!busy || investorsLoading} onClick={() => run(true)}>Confirm merge</button>
            <button type="button" disabled={!!busy} onClick={() => setConfirmation(false)}>Cancel</button></div>
        </div>}
    </div>}
  </section>;
}
